import { FC } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey } from '@fortawesome/free-solid-svg-icons';
import { useShareboxGetCaseKeyStatus } from 'api/sharebox/sharebox';
import useHasAccessToSharebox from 'hooks/useHasAccessToSharebox';
import { formatDayMonth, formatTime } from 'utils/date-helpers';

const Banner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 8px 16px;
  margin-bottom: 10px;
  background: ${({ theme }) => theme.colors.background.highlight};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: 3px;
  color: ${({ theme }) => theme.colors.foreground.primary};
`;

const BannerLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

type Props = {
  caseId?: number;
};

/**
 * Full-width info strip shown at the top of the case view when the car key
 * currently sits in a Sharebox locker. Renders nothing while loading, on
 * error, when there is no current (isCurrent) reservation row, or when the
 * viewer is outside the Sharebox pilot (see useHasAccessToSharebox).
 */
const ShareboxKeyBanner: FC<Props> = ({ caseId }) => {
  // Backend gatear samma lista på endpointen; den här kollen sparar bara ett
  // anrop som ändå skulle svara tomt för användare utanför piloten.
  const hasAccess = useHasAccessToSharebox();
  const keyStatusCall = useShareboxGetCaseKeyStatus(
    { caseId },
    {
      query: {
        enabled: caseId !== undefined && hasAccess,
      },
    }
  );

  const currentRows = (keyStatusCall.data ?? []).filter((row) => row.isCurrent);

  if (currentRows.length === 0) return null;

  return (
    <Banner>
      {currentRows.map((row) => (
        <BannerLine key={row.shareboxReservationRowId}>
          <FontAwesomeIcon icon={faKey} />
          <span>
            Nyckeln ligger i Sharebox {row.hubName}, lucka {row.lockerNo}
            {row.keyDroppedInAt &&
              ` · inlagd ${formatDayMonth(row.keyDroppedInAt)} ${formatTime(
                row.keyDroppedInAt
              )}`}
          </span>
        </BannerLine>
      ))}
    </Banner>
  );
};

export default ShareboxKeyBanner;
